import type { MessageKey } from './en';

const de: Record<MessageKey, string> = {
  lang_switch_label: 'Sprache',

  global_empty_state_title: 'Noch nichts hier',
  global_empty_state_description: 'Lege den ersten Eintrag an, um zu starten.',
  pagination_go_to_page: 'Gehe zu Seite',
  pagination_items_per_page: 'Einträge pro Seite',
  pagination_first_page: 'Erste Seite',
  pagination_previous_page: 'Vorherige Seite',
  pagination_next_page: 'Nächste Seite',
  pagination_last_page: 'Letzte Seite',

  auth_hero_tagline: 'Rechnung hochladen. In Raten finanzieren.',

  login_welcome_back: 'Willkommen zurück',
  login_subtitle: 'Melde dich mit E-Mail und Passwort an.',
  login_email_label: 'E-Mail',
  login_password_label: 'Passwort',
  login_button: 'Anmelden',
  login_forgot_password: 'Passwort vergessen?',
  login_reset_it: 'Zurücksetzen',
  login_error_user_not_found: 'E-Mail und Passwort stimmen nicht mit unseren Daten überein.',
  login_error_wrong_password: 'Falsches Passwort.',
  login_error_invalid_email: 'Ungültige E-Mail-Adresse.',
  login_error_user_disabled: 'Dieses Konto wurde deaktiviert.',
  login_error_login_failed: 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.',
  login_unexpected_error: 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',
  login_need_account: 'Noch kein Konto?',
  login_go_signup: 'Registrieren',

  signup_create_account: 'Konto erstellen',
  signup_get_started_description: 'Verwende dieselbe E-Mail wie in der Produktion.',
  signup_display_name_label: 'Anzeigename (optional)',
  signup_email_label: 'E-Mail',
  signup_password_label: 'Passwort',
  signup_confirm_password_label: 'Passwort bestätigen',
  signup_passwords_do_not_match: 'Passwörter stimmen nicht überein.',
  signup_button: 'Konto erstellen',
  signup_already_have_account: 'Bereits ein Konto?',
  signup_login_link: 'Anmelden',
  signup_email_already_in_use: 'Diese E-Mail ist bereits registriert.',
  signup_weak_password: 'Passwort ist zu schwach. Mindestens 6 Zeichen verwenden.',
  signup_unexpected_error: 'Konto konnte nicht erstellt werden. Bitte erneut versuchen.',
  signup_firestore_hint:
    'Erstellt ein Firestore-Dokument unter users/{uid}. Stelle sicher, dass deine Sicherheitsregeln dem angemeldeten Nutzer erlauben, sein Profil zu schreiben.',

  reset_password_title: 'Passwort zurücksetzen',
  reset_password_subtitle: 'Wir senden dir einen Link, um ein neues Passwort zu wählen.',
  reset_password_email_label: 'E-Mail',
  reset_password_send_button: 'Link senden',
  reset_password_remembered_password: 'Passwort wieder eingefallen?',
  reset_password_back_to_login: 'Zur Anmeldung',
  reset_password_success_message:
    'Wenn ein Konto für diese E-Mail existiert, erhältst du in Kürze einen Link.',
  reset_password_error_unauthorized_domain:
    'Diese Domain ist für Passwort-Reset nicht autorisiert. Füge sie unter Firebase Console → Authentication → Settings → Authorized domains hinzu.',
  reset_password_error_no_account_found: 'Kein Konto für diese E-Mail gefunden.',
  reset_password_error_invalid_email: 'Bitte gib eine gültige E-Mail-Adresse ein.',
  reset_password_error_link_not_sent: 'Link konnte nicht gesendet werden. Bitte später erneut versuchen.',
  reset_password_unexpected_error: 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',
  reset_password_action_url_hint:
    'Setze die Firebase Auth Action URL (Continue URL) auf /confirm-password auf dieser Origin.',

  reset_password_new_password_title: 'Neues Passwort wählen',
  reset_password_new_password_subtitle: 'Gib dein neues Passwort ein und bestätige es.',
  reset_password_verifying: 'Reset-Link wird geprüft…',
  reset_password_error_code_invalid: 'Dieser Reset-Link ist ungültig oder wurde bereits verwendet.',
  reset_password_error_code_expired: 'Dieser Reset-Link ist abgelaufen. Fordere einen neuen an.',
  reset_password_new_password_label: 'Neues Passwort',
  reset_password_confirm_password_label: 'Neues Passwort bestätigen',
  reset_password_reset_button: 'Passwort aktualisieren',
  reset_password_success_reset: 'Passwort aktualisiert. Weiterleitung zur Anmeldung…',
  reset_password_error_passwords_mismatch: 'Passwörter stimmen nicht überein.',
  reset_password_error_password_too_short: 'Mindestens 6 Zeichen verwenden.',
  reset_password_error_weak_password: 'Passwort ist zu schwach. Wähle ein stärkeres.',
  reset_password_error_generic: 'Passwort konnte nicht zurückgesetzt werden. Bitte erneut versuchen.',
  reset_password_request_new_link: 'Neuen Reset-Link anfordern',

  nav_home: 'Übersicht',
  nav_cases: 'Finanzierungsfälle',
  nav_customers: 'Kunden',
  nav_documents: 'Angebote & Rechnungen',
  nav_installments: 'Ratenoptionen',
  nav_branding: 'Branding',
  nav_audit: 'Audit-Log',
  nav_settings: 'Einstellungen',
  nav_docs: 'Doku',
  nav_changelog: 'Changelog',
  nav_clients: 'Kundenunternehmen',
  nav_lenders: 'Finanzierungspartner',
  nav_financing_options: 'Finanzierungsoptionen',
  nav_team: 'Team',

  common_save: 'Speichern',
  common_cancel: 'Abbrechen',
  common_create: 'Anlegen',
  common_edit: 'Bearbeiten',
  common_archive: 'Archivieren',
  common_error_generic: 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',
  common_loading: 'Laden…',
  common_search: 'Suchen',
  common_no_results: 'Keine Treffer.',
  common_status: 'Status',
  common_actions: 'Aktionen',
  common_back: 'Zurück',
  common_next: 'Weiter',
  common_publish: 'Veröffentlichen',
  common_copy_link: 'Link kopieren',

  role_client_admin: 'Admin',
  role_client_user: 'Mitarbeiter',
  role_internal_admin: 'Craftin Admin',
  role_lender_admin: 'Lender Admin',
  topbar_your_role: 'Deine Rolle',

  create_org_title: 'Unternehmen einrichten',
  create_org_subtitle:
    'Ein Unternehmen ist dein Craftin-Arbeitsbereich: Kunden, Belege und Finanzierungsfälle liegen hier.',
  create_org_name_label: 'Unternehmensname',
  create_org_legal_name_label: 'Firma (rechtlich)',
  create_org_submit: 'Unternehmen anlegen',
  create_org_error: 'Unternehmen konnte nicht angelegt werden. Bitte erneut versuchen.',
  create_org_or: 'Oder neues Unternehmen anlegen:',
  invites_title: 'Du wurdest eingeladen',
  invites_role_label: 'Rolle',
  invites_accept: 'Annehmen',
  invites_decline: 'Ablehnen',
  invites_error: 'Einladung konnte nicht verarbeitet werden. Bitte erneut versuchen.',

  home_title: 'Unternehmensübersicht',
  home_cases_open: 'Offene Fälle',
  home_cases_submitted: 'Eingereicht',
  home_volume: 'Finanzierungsvolumen (genehmigt)',
  home_customers: 'Kunden',
  home_docs_review: 'Belege zur Prüfung',
  home_expiring: 'Läuft bald ab',
  home_cta_case: 'Neuer Finanzierungsfall',
  home_cta_document: 'Beleg hochladen',
  home_empty: 'Lege einen Kunden an und lade eine Rechnung hoch, um den ersten Fall zu starten.',

  customers_title: 'Kunden',
  customers_add: 'Kunde anlegen',
  customers_col_name: 'Name',
  customers_col_email: 'E-Mail',
  customers_col_type: 'Typ',
  customers_col_status: 'Status',
  customers_empty: 'Noch keine Kunden. Lege den Auftraggeber an, für den finanziert wird.',
  customer_form_create: 'Neuer Kunde',
  customer_form_edit: 'Kunde bearbeiten',
  customer_type_consumer: 'Privat',
  customer_type_business: 'Gewerbe',
  customer_first_name: 'Vorname',
  customer_last_name: 'Nachname',
  customer_business_name: 'Firmenname',
  customer_email: 'E-Mail',
  customer_phone: 'Telefon',
  customer_city: 'Ort',
  customer_duplicate_warn: 'Ein Kunde mit dieser E-Mail existiert bereits in deinem Unternehmen.',

  documents_title: 'Angebote & Rechnungen',
  documents_add: 'Neuer Beleg',
  documents_upload: 'Hochladen & extrahieren',
  documents_col_number: 'Nummer',
  documents_col_type: 'Typ',
  documents_col_customer: 'Kunde',
  documents_col_total: 'Summe',
  documents_col_extraction: 'Extraktion',
  documents_col_status: 'Status',
  documents_empty: 'Lade ein Angebot oder eine Rechnung hoch, oder lege sie manuell an.',
  document_form_create: 'Beleg anlegen',
  document_form_edit: 'Beleg prüfen',
  document_type_quote: 'Angebot',
  document_type_invoice: 'Rechnung',
  document_number: 'Belegnummer',
  document_issue_date: 'Belegdatum',
  document_total_gross: 'Bruttosumme',
  document_confirm: 'Extraktion bestätigen',
  document_extract: 'Extraktion starten',
  document_line_desc: 'Beschreibung',
  document_needs_customer: 'Zuerst einen Kunden wählen',

  cases_title: 'Finanzierungsfälle',
  cases_add: 'Neuer Fall',
  cases_col_number: 'Fall',
  cases_col_customer: 'Kunde',
  cases_col_amount: 'Betrag',
  cases_col_status: 'Status',
  cases_col_expires: 'Ablauf',
  cases_empty: 'Noch keine Finanzierungsfälle. Starte mit einem geprüften Beleg.',
  case_wizard_title: 'Finanzierungsfall anlegen',
  case_wizard_step_customer: 'Kunde',
  case_wizard_step_document: 'Beleg',
  case_wizard_step_financing: 'Finanzierung',
  case_wizard_step_publish: 'Veröffentlichen',
  case_wizard_amount: 'Finanzierungsbetrag',
  case_wizard_plans: 'Verfügbare Ratenoptionen',
  case_wizard_expires_days: 'Link gültig (Tage)',
  case_wizard_title_field: 'Titel für den Kunden',
  case_publish_success: 'Fall veröffentlicht. Teile den Checkout-Link mit deinem Kunden.',
  case_link_copied: 'Link kopiert',

  installments_title: 'Ratenoptionen',
  installments_empty: 'Keine aktiven Ratenoptionen. Craftin schaltet Produkte für dein Unternehmen frei.',
  installments_col_name: 'Produkt',
  installments_col_term: 'Laufzeit',
  installments_col_rate: 'Nominalzins',
  installments_col_range: 'Betragsspanne',
  installments_col_status: 'Status',
  installments_months: '{n} Monate',

  branding_title: 'Branding',
  branding_primary: 'Primärfarbe',
  branding_secondary: 'Sekundärfarbe',
  branding_support_email: 'Support-E-Mail',
  branding_support_phone: 'Support-Telefon',
  branding_privacy: 'Datenschutz-URL',
  branding_terms: 'AGB-URL',
  branding_preview: 'Checkout-Vorschau',
  branding_saved: 'Branding gespeichert',
  branding_logo: 'Logo',
  branding_hero_image: 'Titelbild',
  branding_upload_hint: 'PNG, JPG oder SVG — max. 2 MB',
  branding_file_too_large: 'Bitte nutze ein Bild bis 2 MB.',
  branding_whitelabel_title: 'White-Label-Finanzierungscheck',
  branding_whitelabel_hint:
    'Teile diesen Link, damit deine Kundschaft den Finanzierungscheck in deinem Branding durchläuft.',

  audit_title: 'Audit-Log',
  audit_empty: 'Noch keine Audit-Ereignisse.',
  audit_col_when: 'Wann',
  audit_col_actor: 'Akteur',
  audit_col_action: 'Aktion',
  audit_col_object: 'Objekt',

  settings_title: 'Unternehmenseinstellungen',
  settings_company: 'Unternehmensprofil',
  settings_members: 'Team',
  settings_invite: 'Nutzer einladen',
  settings_invite_email: 'E-Mail',
  settings_invite_role: 'Rolle',
  settings_legal_name: 'Firma (rechtlich)',
  settings_email: 'Unternehmens-E-Mail',
  settings_city: 'Ort',
  settings_street: 'Straße',
  settings_house_number: 'Nr.',
  settings_postal: 'PLZ',

  team_title: 'Team',
  team_col_name: 'Name',
  team_col_email: 'E-Mail',
  team_col_role: 'Rolle',
  team_col_org: 'Organisation',
  team_filter_role: 'Rolle',
  team_filter_all: 'Alle Rollen',
  team_empty: 'Keine Nutzer gefunden.',
  team_invite: 'Nutzer einladen',
  team_invite_hint: 'Die Einladungs-E-Mail enthält einen persönlichen Link, um {org} beizutreten.',
  team_invite_org: 'Organisation',
  team_invite_org_placeholder: 'Organisation wählen',
  team_invite_success: 'Einladung an {email} gesendet.',
  team_invite_error_member: 'Diese E-Mail gehört bereits zu einem Mitglied der Organisation.',
  team_invite_copy_link: 'Einladungslink kopieren',
  team_invite_link_copied: 'Link kopiert',
  team_invite_done: 'Fertig',
  team_pending_title: 'Ausstehende Einladungen',

  invite_verifying: 'Einladung wird geprüft…',
  invite_title: 'Du wurdest eingeladen',
  invite_intro: '{inviter} hat dich eingeladen, {org} auf Craftin beizutreten.',
  invite_invalid: 'Dieser Einladungslink ist ungültig oder nicht mehr verfügbar.',
  invite_expired: 'Diese Einladung ist abgelaufen. Bitte fordere eine neue an.',
  invite_already_accepted: 'Diese Einladung wurde bereits angenommen.',
  invite_declined_done: 'Du hast die Einladung abgelehnt.',
  invite_mismatch:
    'Diese Einladung wurde an {email} gesendet. Bitte melde dich ab und fahre mit dieser Adresse fort.',
  invite_sign_out: 'Abmelden',
  invite_accept_and_join: 'Konto erstellen & beitreten',
  invite_have_account: 'Schon ein Konto?',
  invite_sign_in: 'Anmelden',
  invite_accept_error: 'Einladung konnte nicht verarbeitet werden. Bitte erneut versuchen.',
  invite_already_member_error: 'Dein Konto gehört bereits zu einem anderen Unternehmens-Workspace.',
  invite_go_home: 'Zu meinem Workspace',
  invite_go_login: 'Zur Anmeldung',

  clients_title: 'Kundenunternehmen',
  clients_add: 'Kunde hinzufügen',
  clients_empty: 'Noch keine Kundenunternehmen.',
  clients_col_name: 'Unternehmen',
  clients_col_city: 'Ort',
  clients_col_email: 'E-Mail',
  clients_col_status: 'Status',
  clients_form_create: 'Neues Kundenunternehmen',
  clients_form_edit: 'Kundenunternehmen bearbeiten',
  clients_legal_form: 'Rechtsform',

  lenders_title: 'Lender',
  lenders_add: 'Lender hinzufügen',
  lenders_empty: 'Noch keine Lender.',
  lenders_col_name: 'Name',
  lenders_col_integration: 'Integration',
  lenders_col_status: 'Status',
  lenders_form_create: 'Neuer Lender',
  lenders_form_edit: 'Lender bearbeiten',
  lenders_customer_types: 'Kundentypen',
  lenders_customer_both: 'Privat & Gewerbe',
  lenders_deactivate: 'Deaktivieren',

  financing_options_title: 'Finanzierungsoptionen',
  financing_options_add: 'Option hinzufügen',
  financing_options_empty: 'Noch keine Finanzierungsoptionen.',
  financing_options_col_lender: 'Lender',
  financing_options_form_create: 'Neue Finanzierungsoption',
  financing_options_form_edit: 'Finanzierungsoption bearbeiten',
  financing_options_scope: 'Geltungsbereich',
  financing_options_scope_global: 'Global',
  financing_options_scope_company: 'Unternehmen',
  financing_options_term_months: 'Laufzeit (Monate)',
  financing_options_rate_bps: 'Nominalzins (bps)',
  financing_options_min_amount: 'Min. Betrag (EUR)',
  financing_options_max_amount: 'Max. Betrag (EUR)',

  checkout_loading: 'Checkout wird geladen…',
  checkout_invalid: 'Dieser Finanzierungslink ist ungültig oder nicht mehr verfügbar.',
  checkout_expired: 'Dieser Finanzierungslink ist abgelaufen.',
  checkout_revoked: 'Dieser Finanzierungslink wurde widerrufen.',
  checkout_welcome: 'Projekt finanzieren',
  checkout_review_doc: 'Dein Angebot',
  checkout_select_plan: 'Zahlungsplan wählen',
  checkout_your_details: 'Deine Angaben',
  checkout_consent:
    'Ich willige ein, dass Craftin und der Finanzierungspartner meine Daten zur Prüfung dieses Antrags verarbeiten (Bonitätsprüfung). Details stehen in der Datenschutzerklärung.',
  checkout_submit: 'Antrag absenden',
  checkout_monthly: 'Monatlich',
  checkout_total: 'Gesamtbetrag',
  checkout_term: 'Laufzeit',
  checkout_result_approved: 'Genehmigt',
  checkout_result_rejected: 'Nicht genehmigt',
  checkout_result_pending: 'In Prüfung',
  checkout_result_info: 'Weitere Angaben erforderlich',
  checkout_result_error: 'Technisches Problem — bitte das Handwerksunternehmen kontaktieren.',
  checkout_secure: 'Sichere Verbindung',
  checkout_powered: 'Finanzierungs-Checkout von Craftin',

  check_title: 'Rechnung finanzieren',
  check_subtitle: 'Lade deine Handwerksrechnung hoch oder gib den Betrag ein, wir prüfen, deine Ratenfinanzierung.',
  check_subtitle_branded: 'Lade deine Rechnung von {company} hoch oder gib den Betrag ein, wir prüfen deine Ratenfinanzierung.',
  check_powered_by: 'Powered by Craftin',
  check_step_1: 'Schritt 1 von 2 — Deine Rechnung',
  check_step_2: 'Schritt 2 von 2 — Dein Konto',
  check_mode_upload: 'Rechnung hochladen',
  check_mode_upload_hint: 'PDF oder Foto',
  check_mode_manual: 'Betrag eingeben',
  check_mode_manual_hint: 'Keine Datei zur Hand',
  check_file_label: 'Rechnung',
  check_file_placeholder: 'Keine Datei ausgewählt',
  check_file_hint: 'PDF, JPG oder PNG — max. 20 MB',
  check_file_browse: 'Datei wählen',
  check_extracting_title: 'Rechnung wird analysiert …',
  check_extracting_hint: 'Betrag und Betrieb werden automatisch ausgelesen.',
  check_review_title: 'Erkannte Rechnungsdaten',
  check_review_provider: 'Betrieb',
  check_review_city: 'Ort',
  check_review_amount: 'Betrag',
  check_review_description: 'Leistung',
  check_review_hint: 'Automatisch erkannt — bitte kurz prüfen.',
  check_review_edit: 'Angaben korrigieren',
  check_review_change_file: 'Andere Datei wählen',
  check_extract_failed: 'Wir konnten die Rechnung nicht automatisch auslesen. Bitte gib die Angaben manuell ein.',
  check_extract_incomplete: 'Wir konnten nicht alle Angaben erkennen. Bitte ergänze die fehlenden Felder.',
  check_manual_file_attached: 'Rechnung angehängt:',
  check_amount_label: 'Rechnungsbetrag (EUR)',
  check_amount_optional_label: 'Rechnungsbetrag (EUR, optional)',
  check_provider_label: 'Handwerksbetrieb auf der Rechnung',
  check_provider_placeholder: 'z. B. Müller Sanitär GmbH',
  check_provider_city_label: 'Ort des Betriebs (optional)',
  check_description_label: 'Was wurde gemacht? (optional)',
  check_account_title: 'Konto anlegen',
  check_account_subtitle: 'Hier verfolgst du deine Finanzierungsanfrage und das Ergebnis.',
  check_signed_in_as: 'Angemeldet als',
  check_consent: 'Ich willige ein, dass Craftin und seine Finanzierungspartner meine Daten und die Rechnung zur Prüfung meiner Finanzierungsmöglichkeiten verarbeiten. Details stehen in der Datenschutzerklärung.',
  check_submit: 'Finanzierung prüfen',
  check_error_provider: 'Bitte gib den Handwerksbetrieb von deiner Rechnung an.',
  check_error_file_required: 'Bitte wähle eine Rechnungsdatei oder wechsle zur Betragseingabe.',
  check_error_file_invalid: 'Bitte nutze ein PDF, JPG oder PNG bis 20 MB.',
  check_error_amount: 'Bitte gib einen gültigen Betrag ein, z. B. 2.500,00.',
  check_error_consent: 'Bitte stimme der Datenverarbeitung zu.',
  check_error_email_in_use: 'Diese E-Mail ist bereits registriert.',
  check_error_already_member: 'Dein Konto gehört bereits zu einem Firmen-Workspace. Bitte melde dich an und kontaktiere den Support.',
  check_upload_failed: 'Deine Anfrage wurde gespeichert, aber der Rechnungs-Upload ist fehlgeschlagen. Du kannst die Rechnung später in deinem Konto ergänzen.',
  check_success_title: 'Anfrage eingegangen',
  check_success_body: 'Wir prüfen jetzt, ob wir eine Finanzierung für dich bekommen. Das Ergebnis siehst du in deinem Konto.',
  check_success_reference: 'Deine Referenz',
  check_success_cta: 'Zu meinem Konto',

  docs_title: 'Dokumentation',
  changelog_title: 'Changelog',
};

export default de;
